import {
  createDefaultRandomConstraints,
  type AiVerificationDefaultCandidateCriteria,
  type AiVerificationDistribution,
  type AiVerificationRandomConstraints,
} from '@citizen-walk/shared-types';

/**
 * 配分デフォルト 専用フォーム (Admin Wave AW4、docs/108 High #8)。
 *
 * 旧 `old2/apps/admin/src/pages/VerificationTemplateEditorPage.tsx` の `DefaultDistributionSection`
 * を v2 に踏襲。テンプレ編集 / プリセット編集の「配分デフォルト」を生 JSON textarea から
 * 構造化フォーム (defaultDistribution select + random 制約 + candidateCriteria + endAtOffsetDays) に戻す。
 *
 * controlled component: 親が `DefaultDistributionValue` を保持し、`onChange(next)` で丸ごと受け取る。
 * 「未指定」フィールドは object から落として返す (undefined キーは Firestore が拒否するため)。
 */

export interface DefaultDistributionValue {
  defaultDistribution?: AiVerificationDistribution;
  defaultRandomConstraints?: AiVerificationRandomConstraints;
  defaultCandidateCriteria?: AiVerificationDefaultCandidateCriteria;
  defaultEndAtOffsetDays?: number;
}

const DIST_LABELS: Record<AiVerificationDistribution, string> = {
  free: '自由検証（参加者が任意の AI 解析済投稿を選ぶ）',
  all_same: '全員同一（管理者が選んだ投稿群を全員に出題）',
  random_per_user: 'ランダム配分（各人に最大 N 件を自動配分・自分→他者の順）',
  manual: '個別アサイン（管理者が個別指定）',
};

export interface DefaultDistributionSectionProps {
  value: DefaultDistributionValue;
  onChange: (next: DefaultDistributionValue) => void;
  disabled?: boolean;
}

export function DefaultDistributionSection({
  value,
  onChange,
  disabled = false,
}: DefaultDistributionSectionProps) {
  const dist = value.defaultDistribution;
  const constraints = value.defaultRandomConstraints ?? createDefaultRandomConstraints();
  const criteria = value.defaultCandidateCriteria;

  function setDistribution(v: '' | AiVerificationDistribution) {
    if (v === '') {
      const { defaultDistribution: _d, defaultRandomConstraints: _r, ...rest } = value;
      void _d;
      void _r;
      onChange(rest);
      return;
    }
    if (v !== 'random_per_user') {
      const { defaultRandomConstraints: _r, ...rest } = value;
      void _r;
      onChange({ ...rest, defaultDistribution: v });
    } else {
      onChange({
        ...value,
        defaultDistribution: v,
        defaultRandomConstraints:
          value.defaultRandomConstraints ?? createDefaultRandomConstraints(),
      });
    }
  }

  function patchConstraints(patch: Partial<AiVerificationRandomConstraints>) {
    onChange({ ...value, defaultRandomConstraints: { ...constraints, ...patch } });
  }

  function setCriteriaType(v: '' | AiVerificationDefaultCandidateCriteria['type']) {
    if (v === '') {
      const { defaultCandidateCriteria: _c, ...rest } = value;
      void _c;
      onChange(rest);
    } else {
      onChange({
        ...value,
        defaultCandidateCriteria: { ...(criteria ?? {}), type: v },
      });
    }
  }

  function setSessionScope(
    v: '' | NonNullable<AiVerificationDefaultCandidateCriteria['sessionScope']>,
  ) {
    if (!criteria) return;
    if (v === '') {
      onChange({ ...value, defaultCandidateCriteria: { type: criteria.type } });
    } else {
      onChange({ ...value, defaultCandidateCriteria: { ...criteria, sessionScope: v } });
    }
  }

  function setEndAtOffsetDays(raw: string) {
    if (raw === '') {
      const { defaultEndAtOffsetDays: _e, ...rest } = value;
      void _e;
      onChange(rest);
    } else {
      onChange({ ...value, defaultEndAtOffsetDays: Math.max(0, parseInt(raw, 10) || 0) });
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="default-distribution-section">
      <p className="text-xs text-slate-500">
        ここで設定した値は、地区が配分セットを作成する際にこのテンプレを選んだ時点で
        <strong>プリセットとして自動入力</strong>されます。地区側で必要に応じて上書きできます。
      </p>

      {/* 配分パターンのデフォルト */}
      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">
          配分パターンのデフォルト (defaultDistribution)
        </span>
        <select
          value={dist ?? ''}
          onChange={(e) => setDistribution(e.target.value as '' | AiVerificationDistribution)}
          disabled={disabled}
          aria-label="defaultDistribution"
          className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
        >
          <option value="">— 未指定（地区側が自由に選択）</option>
          {(Object.keys(DIST_LABELS) as AiVerificationDistribution[]).map((v) => (
            <option key={v} value={v}>
              {DIST_LABELS[v]}
            </option>
          ))}
        </select>
      </label>

      {/* random_per_user 用の制約デフォルト */}
      {dist === 'random_per_user' && (
        <div className="border border-slate-200 rounded p-3 bg-slate-50 grid grid-cols-1 gap-3">
          <h4 className="text-sm font-bold text-slate-800">ランダム配分の制約（デフォルト値）</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">1人あたりの最大割当数（上限）</span>
              <input
                type="number"
                min={1}
                max={50}
                value={constraints.minPostsPerUser}
                onChange={(e) =>
                  patchConstraints({
                    minPostsPerUser: Math.max(1, parseInt(e.target.value, 10) || 1),
                  })
                }
                disabled={disabled}
                aria-label="defaultRandomConstraints.minPostsPerUser"
                className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">
                うち自分の投稿の最低数 (minOwnPosts、preferOwnPosts=OFF 時)
              </span>
              <input
                type="number"
                min={0}
                max={50}
                value={constraints.minOwnPosts}
                onChange={(e) =>
                  patchConstraints({
                    minOwnPosts: Math.max(0, parseInt(e.target.value, 10) || 0),
                  })
                }
                disabled={disabled}
                aria-label="defaultRandomConstraints.minOwnPosts"
                className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 gap-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!constraints.preferOwnPosts}
                onChange={(e) => patchConstraints({ preferOwnPosts: e.target.checked })}
                disabled={disabled}
                aria-label="defaultRandomConstraints.preferOwnPosts"
              />
              自分の投稿で上限まで埋める
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!constraints.preferLessVerified}
                onChange={(e) => patchConstraints({ preferLessVerified: e.target.checked })}
                disabled={disabled}
                aria-label="defaultRandomConstraints.preferLessVerified"
              />
              票が偏らないよう既検証少を優先
            </label>
          </div>
        </div>
      )}

      {/* 候補絞り込み基準のデフォルト */}
      <div className="border border-slate-200 rounded p-3 bg-slate-50 grid grid-cols-1 gap-3">
        <h4 className="text-sm font-bold text-slate-800">
          候補絞り込み基準 (defaultCandidateCriteria)
        </h4>
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">候補プールの種類</span>
          <select
            value={criteria?.type ?? ''}
            onChange={(e) =>
              setCriteriaType(e.target.value as '' | AiVerificationDefaultCandidateCriteria['type'])
            }
            disabled={disabled}
            aria-label="defaultCandidateCriteria.type"
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
          >
            <option value="">— 未指定</option>
            <option value="all_ai_done">all_ai_done（地区内の AI 解析済全投稿）</option>
            <option value="session_only">session_only（セッション内のみ）</option>
            <option value="manual_pick">manual_pick（admin 手動選定）</option>
          </select>
        </label>
        {criteria?.type === 'session_only' && (
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs text-slate-500">セッションスコープ</span>
            <select
              value={criteria.sessionScope ?? ''}
              onChange={(e) =>
                setSessionScope(
                  e.target.value as
                    | ''
                    | NonNullable<AiVerificationDefaultCandidateCriteria['sessionScope']>,
                )
              }
              disabled={disabled}
              aria-label="defaultCandidateCriteria.sessionScope"
              className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            >
              <option value="">— 未指定</option>
              <option value="set_session">set_session（配分セットに紐づくセッション）</option>
              <option value="all_sessions">all_sessions（全セッション）</option>
            </select>
          </label>
        )}
      </div>

      {/* 期限デフォルト */}
      <label className="grid grid-cols-1 gap-1">
        <span className="text-xs text-slate-500">期限デフォルト (defaultEndAtOffsetDays)</span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={365}
            value={value.defaultEndAtOffsetDays ?? ''}
            onChange={(e) => setEndAtOffsetDays(e.target.value)}
            disabled={disabled}
            placeholder="未指定"
            aria-label="defaultEndAtOffsetDays"
            className="w-32 min-h-touch px-3 border border-slate-300 rounded text-sm"
          />
          <span className="text-xs text-slate-500">日後を期限初期値に（開始日時 + N 日）</span>
        </div>
      </label>
    </div>
  );
}
